/**
 * ⟦A-4 décision 4 + arbitrage E⟧ / **M2.10** — **Le marché des freelances : arrêter de tout
 * faire soi-même.**
 *
 * SPEC-MOTEUR §2.2 le décrit depuis le premier jour et personne ne l'avait construit : *« le
 * marché propose, par étape du pipeline, des freelances porteurs d'un prix et d'une cote
 * (0-100). Embaucher = sauter le mini-jeu et prendre la cote du freelance ± variance. »* Ce
 * module **remplace** la délégation transitoire « moyenne des 10 derniers scores − 10 »
 * (`publish.scoreDelegue`, retirée dans la même passe) et branche enfin les deux nœuds qui se
 * payaient sans rien faire — *Monteur salarié* (12 PC + 1 000 ₵) et *Le Studio* (8 PC +
 * 2 000 ₵).
 *
 * ── Les deux règles de conception, non négociables ⟦A-45⟧ ────────────────────
 *
 * **1. Déléguer coûte de la QUALITÉ, pas seulement de l'argent.** Aucune cote ne dépasse
 * `COTE_MAX = 82` — six points sous la moyenne d'une exécution forte — et le
 * schéma Zod le fait respecter au boot, cote d'arrivée de la pépite comprise. Un joueur qui
 * joue bien reste **toujours** meilleur que le meilleur freelance : les mini-jeux ne se
 * vident jamais, on choisit de souffler en le payant.
 *
 * **2. Ça s'entretient, ça ne se déverrouille pas.** Le prix est prélevé **à chaque vidéo**,
 * et il suit la notoriété (`MULT_PRIX_NOTORIETE`) : une équipe complète de haut de gamme
 * cesse d'être payable en fin de partie, et on reprend les ciseaux. C'est la tension voulue,
 * pas un accident d'équilibrage.
 *
 * ── La pépite ──────────────────────────────────────────────────────────────────
 *
 * Un jeune pas cher, à cote basse, **dont la cote monte à chaque fois qu'on lui refait
 * confiance** (`PEPITE_GAIN_PAR_EMBAUCHE`, plafonné par `PEPITE_EMBAUCHES_MAX`). Et une fois
 * embauchée, **elle ne quitte plus le marché** : c'est la seule personne du lot sur qui on
 * peut compter, tout le reste tourne. Un pari de confiance qui devient, en fin de partie, la
 * seule équipe encore payable.
 *
 * ── Le tarif d'entrée ⟦A-86⟧ ───────────────────────────────────────────────────
 *
 * Mesuré ⟦A-78 §4 · A-80 §3⟧ : la porte au Grand Saut ouvre le marché à J107 / 1 009 abonnés
 * avec ~600 ₵ en poche — et un joueur qui embauche **sans stratégie** y trouvait, une fenêtre
 * sur dix, rien sous 85 ₵ l'étape : la caisse partait le jour où elle naissait (survie
 * prudents 0,90). Mathieu maintient la porte (c'est une raison de jeu) et demande **un tarif
 * d'entrée** : par étape, une personne **toujours là** (`tarif_entree`), jamais tirée, jamais
 * sortie, pas chère, et qui rend *« une vidéo moyenne, jamais une bonne »* — **prix bas ⇔
 * cote basse**, sinon les nœuds qui approfondissent l'offre ⟦A-78 §2⟧ ne vendraient rien.
 *
 * Pourquoi « toujours là » plutôt qu'une personne de plus dans le tirage : à deux offres tirées
 * parmi six, le bon marché serait là une fenêtre sur trois — *accessible* par chance, pas dès
 * l'ouverture. Et pourquoi ces cinq-là (le stagiaire, le cousin, le monteur de mariages, le
 * Photoshop cracké, le bot) plutôt que cinq nouveaux : un tarif d'entrée à cote moyenne aurait
 * rendu l'ancien bas de gamme (cote 50-55, 20-40 ₵) strictement dominé — cinq fiches mortes.
 * Ce sont les mêmes gens, à la place qui est la leur : le coup de main qu'on trouve toujours.
 *
 * ── Un marché DÉRIVÉ, pas un marché stocké ─────────────────────────────────────
 *
 * L'offre est une **forme close** de `(seed, période, étapes ouvertes, embauches)` — même
 * choix de conception que la courbe des rivaux (« courbe simulée, pas une run parallèle ») :
 * aucune accumulation, aucun état à faire avancer, donc **aucune dérive possible**. La
 * conséquence qui compte est l'état neutre d'⟦A-10⟧ : le seul champ d'état de ce module est
 * `state.freelances`, et il n'est écrit **qu'au moment où le joueur embauche pour de bon**.
 * Un joueur qui n'embauche personne joue un jeu rigoureusement identique — pas « presque
 * identique », identique : rien n'est écrit, aucun tirage n'est consommé.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random` — tout l'aléa
 * passe par `draw(seed, …, "pnj_pick")` (voir `INDEX_*` pour le partage de l'espace d'index).
 */
import { H_JOUR } from "./clock.js";
import { argentOuvert } from "./escalier.js";
import { hasCapability } from "./effects.js";
import { draw, uniformFloat } from "./rng.js";
import { ATELIER_STAGES, } from "./types.js";
/**
 * Extrait le vivier d'un contenu qui le porte. Contenu sans `freelances` ⇒ vivier vide ⇒
 * marché vide : l'absence est **visible dans les tests** plutôt que silencieusement
 * permissive (même shim que `rivauxDuContenu` et `evenementsDuContenu`).
 */
export function freelancesDuContenu(content) {
    return content.freelances ?? [];
}
// ─────────────────────────────────────────────────────────────────────────────
// Constantes d'équilibrage — les ✎ que SPEC-MOTEUR §2.2 laissait à poser
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦A-45 règle 1⟧ Le plafond absolu d'une cote, **recopié** de `content/schema.ts` pour
 * garder `engine/` libre de Zod (même procédé que `TREND_MULT_MOTEUR`). Il n'est PAS
 * ré-exporté sous ce nom : le barrel exporte déjà celui du schéma, et deux constantes
 * publiques du même nom laisseraient un appelant choisir la mauvaise. Un test verrouille
 * l'égalité des deux sources.
 */
const COTE_MAX = 82;
/** La même valeur, sous le nom que le reste du moteur peut lire sans ambiguïté. */
export const COTE_FREELANCE_PLAFOND = COTE_MAX;
/** Ce qu'une pépite gagne en cote à chaque embauche (✎). */
export const PEPITE_GAIN_PAR_EMBAUCHE = 6;
/** Au bout de combien d'embauches elle a tout donné (✎) — 5 × 6 = +30 points. */
export const PEPITE_EMBAUCHES_MAX = 5;
/**
 * Durée d'une **fenêtre de marché** : au-delà, l'offre a tourné (✎ 7 jours simulés).
 *
 * Calé sur le rythme mesuré de la campagne : ~1 vidéo tous les 3 jours simulés dans la
 * tranche 1 k → 10 k, donc **deux publications environ par fenêtre**. Assez court pour que
 * « le type d'hier n'est plus là » se voie, assez long pour qu'un plan de production tienne.
 */
export const FENETRE_MARCHE_H = 7 * H_JOUR;
/** Combien de non-fidélisés le marché montre par étape ouverte (✎). */
export const OFFRES_PAR_ETAPE = 2;
/**
 * ⟦A-45 règle 2⟧ **Le tarif suit la notoriété.** Un monteur ne facture pas une chaîne de
 * 3 000 abonnés comme une chaîne de 800 000 — et sans cette table, l'équipe complète devient
 * gratuite dès 100 k (mesuré : ~5 400 ₵ de marge nette par vidéo dans la tranche 100 k → 1 M
 * contre ~20 ₵ dans la tranche 1 k → 10 k). *« Une équipe se paie ; si on ne peut plus la
 * payer, on reprend les ciseaux. »* La table est ce qui rend cette phrase vraie.
 *
 * Lue au palier d'abonnés courant, seuils croissants, premier seuil satisfait retenu.
 */
export const MULT_PRIX_NOTORIETE = [
    [1_000_000, 12],
    [100_000, 6],
    [10_000, 3],
    [0, 1],
];
// ─────────────────────────────────────────────────────────────────────────────
// Espace d'index du purpose `pnj_pick` — partagé avec `rivaux.ts`
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⚠️ **Aucun purpose n'est ajouté** : SPEC-MOTEUR §3.2 en tient une liste FERMÉE, et
 * `pnj_pick` — *« tirage des créateurs PNJ **et de leurs paramètres** »* — couvre exactement
 * ce que fait ce module. Un freelance EST un PNJ : c'est même toute l'intention d'⟦A-4⟧, une
 * collection vivante faite de gens.
 *
 * `rivaux.ts` occupe les index **0-110** au bootstrap. Ce module prend deux plages franchement
 * disjointes, pour qu'aucun tirage ne puisse en écraser un autre même si la période de marché
 * tombait sur le `turn` du bootstrap :
 *  - **1 000 + rang×10 + k** — le tirage de l'offre, indexé sur la *période*, pas sur le tour ;
 *  - **2 000 + rang** — la variance d'une livraison, indexée sur le tour de la publication.
 */
const INDEX_OFFRE = 1_000;
const INDEX_LIVRAISON = 2_000;
/** Lecture sûre de l'état du marché — `{}` quand le joueur n'a jamais embauché. */
export function embauches(state) {
    return state.freelances?.embauches ?? {};
}
/** Nombre d'embauches d'un freelance donné sur cette run. */
export function embauchesDe(state, id) {
    return embauches(state)[id] ?? 0;
}
// ─────────────────────────────────────────────────────────────────────────────
// Accès au marché — les deux nœuds qui ne faisaient rien
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Les étapes sur lesquelles le joueur a le droit d'embaucher.
 *
 * ⟦A-78, décision de Mathieu du 21/08⟧ **La porte est le GRAND SAUT : l'argent apparaît, le
 * marché apparaît.** Mesuré avant l'avenant : l'ancienne porte (les deux nœuds profonds de
 * l'arbre) ouvrait à J784–J917, ~565 vidéos, 199 000–330 000 abonnés — *« beaucoup trop
 * tard »*. Le Grand Saut tombe à ~100 vidéos d'une partie médiane, ce que Mathieu demandait
 * (*« à partir de 100 vidéos, un truc comme ça »*), et il est naturellement compatible avec
 * ⟦A-29⟧ (« ce qui coûte de l'argent est dans le marché ») : on n'embauche personne avec un
 * argent qui n'existe pas.
 *
 * **Les deux nœuds ne sont plus la porte — ils améliorent le marché** ⟦A-78 §2, forme
 * proposée par la session d'application dans la grille ⟦A-50⟧ (compétence = permanente) :
 * la PROFONDEUR du vivier⟧ — voir `profondeurOffre`. Leurs capacités gardent leur nom.
 */
export function etapesOuvertes(state, _mods) {
    return argentOuvert(state) ? ATELIER_STAGES : [];
}
/** Le marché est-il ouvert ? ⟦A-78⟧ Oui dès le Grand Saut — plus aucun nœud requis. */
export function marcheOuvert(state, mods) {
    return etapesOuvertes(state, mods).length > 0;
}
/**
 * ⟦A-78 §2 · grille A-50⟧ **Ce que les deux anciens « nœuds-porte » achètent désormais : de
 * la PROFONDEUR d'offre.** Le marché de base montre `OFFRES_PAR_ETAPE` personnes par étape ;
 * *Monteur salarié* en fait voir une de plus au Montage (il connaît le milieu), *Le Studio*
 * une de plus partout (une équipe a un réseau). Les deux se cumulent au Montage.
 *
 * Pourquoi la profondeur plutôt que le prix : c'est un **surensemble strict** — les tirages
 * `k = 0..1` sont les mêmes avec ou sans nœud (mêmes index de `draw`), le nœud AJOUTE un
 * candidat sans jamais changer ni retirer les autres. Aucune offre existante ne bouge, aucune
 * facture ne change : acheter le nœud élargit le choix, il ne réécrit pas le marché. Le prix,
 * lui, reste un levier d'équilibrage de Mathieu (§6.4, le mur d'argent).
 */
export function profondeurOffre(stage, mods) {
    if (mods === undefined)
        return OFFRES_PAR_ETAPE;
    let n = OFFRES_PAR_ETAPE;
    if (hasCapability(mods, "delegation_totale"))
        n += 1;
    if (stage === "montage" && hasCapability(mods, "delegation_montage"))
        n += 1;
    return n;
}
// ─────────────────────────────────────────────────────────────────────────────
// Cote, prix — les deux nombres que le joueur doit pouvoir lire AVANT d'embaucher
// ─────────────────────────────────────────────────────────────────────────────
/**
 * La cote d'un freelance **au moment où on le regarde**. Fixe pour tout le monde, sauf la
 * pépite : elle gagne `PEPITE_GAIN_PAR_EMBAUCHE` par embauche, jusqu'à `PEPITE_EMBAUCHES_MAX`.
 *
 * Le plafond `COTE_MAX` est appliqué ici **aussi**, en plus de l'invariant de
 * schéma : deux gardes valent mieux qu'une quand la règle est « personne ne joue plus les
 * mini-jeux si on se trompe ».
 */
export function coteEffective(f, nbEmbauches) {
    if (!f.pepite)
        return Math.min(COTE_MAX, f.cote);
    const gagne = PEPITE_GAIN_PAR_EMBAUCHE * Math.min(nbEmbauches, PEPITE_EMBAUCHES_MAX);
    return Math.min(COTE_MAX, f.cote + gagne);
}
/** Le multiplicateur de tarif au palier courant (`MULT_PRIX_NOTORIETE`). */
export function multPrixNotoriete(state) {
    for (const [seuil, mult] of MULT_PRIX_NOTORIETE) {
        if (state.subscribers >= seuil)
            return mult;
    }
    return 1;
}
/**
 * Le prix RÉEL d'une embauche, en Clicks — celui qu'il faut afficher, pas `f.prix`.
 *
 * ⚠️ C'est le nombre qui manquait au jeu livré : la délégation transitoire prélevait
 * `50 × 2^(rang−1)` **en silence à la publication**, sans qu'aucun écran ne l'annonce. Le
 * marché ne peut pas se permettre ça — on n'embauche pas quelqu'un dont on découvre le tarif
 * après coup.
 */
export function prixEffectif(f, state) {
    return Math.round(f.prix * multPrixNotoriete(state));
}
// ─────────────────────────────────────────────────────────────────────────────
// L'offre du jour — dérivée, jamais stockée
// ─────────────────────────────────────────────────────────────────────────────
/** La fenêtre de marché courante : l'offre est constante à l'intérieur, elle tourne entre. */
export function periodeMarche(clockH) {
    return Math.floor(clockH / FENETRE_MARCHE_H);
}
/**
 * Les freelances proposés pour une étape, à l'instant `state.clock_h`.
 *
 * Trois populations se cumulent, et l'ordre raconte l'histoire :
 *  1. **les fidélisés** — les pépites déjà embauchées, qui ne quittent plus le marché ;
 *  2. **le tarif d'entrée** ⟦A-86⟧ — la personne de l'étape qui est toujours là, hors
 *     tirage : c'est ce qui garantit qu'au premier marché, il y a de quoi embaucher sans
 *     vider la caisse ;
 *  3. **le tirage de la fenêtre** — `OFFRES_PAR_ETAPE` personnes prises sans remise dans le
 *     reste du vivier de l'étape. C'est là que vit *« un freelance fatigué sort du marché »* :
 *     on ne gère aucune jauge de fatigue, l'offre tourne, et c'est le marché qui la raconte.
 *
 * Rendue **triée par prix croissant** : deux appels au même `clock_h` donnent exactement la
 * même liste, dans le même ordre (déterminisme, §3.3).
 */
export function offresPourEtape(state, content, seed, stage, 
/**
 * La fenêtre de marché à consulter — défaut : celle de `state.clock_h`.
 *
 * ⚠️ **Le prix affiché doit être le prix débité**, et une action n'est pas instantanée :
 * une longue coûte 72 h ⟦A-21⟧, et la fenêtre en fait 168 — presque une publication sur
 * deux traverse une frontière de marché en cours d'action. Sans épinglage, l'offre
 * validée au lancement pouvait avoir tourné à la publication : le freelance choisi
 * disparaissait EN SILENCE (étape gardée à 0, rien de facturé), ou le « moins cher » du
 * défaut changeait de personne et de prix. Le pipeline passe donc la fenêtre du DÉBUT de
 * l'action (`ContexteEmbauche.clock_marche`) — celle que le joueur a vue et que la
 * validation a acceptée.
 */
periode = periodeMarche(state.clock_h), 
/** ⟦A-78 §2⟧ Les nœuds ajoutent de la PROFONDEUR d'offre — voir `profondeurOffre`. */
mods) {
    const vivier = freelancesDuContenu(content).filter((f) => f.etape === stage);
    if (vivier.length === 0)
        return [];
    const compte = embauches(state);
    const lire = (f, fidelise) => {
        const n = compte[f.id] ?? 0;
        return {
            id: f.id,
            nom: f.nom,
            etape: f.etape,
            cote: coteEffective(f, n),
            variance: f.variance,
            prix: prixEffectif(f, state),
            pepite: f.pepite,
            tarif_entree: f.tarif_entree === true,
            embauches: n,
            fidelise,
            description: f.description,
            phrase: f.phrase,
        };
    };
    const fidelises = vivier.filter((f) => f.pepite && (compte[f.id] ?? 0) > 0);
    // ⟦A-86⟧ Le tarif d'entrée ne se tire pas : il est là, et il ne prend la place de personne
    // dans le tirage (il en est retiré, comme les fidélisés — le vivier qui tourne, c'est le reste).
    const entrees = vivier.filter((f) => f.tarif_entree === true && !fidelises.includes(f));
    const dejaLa = new Set([...fidelises, ...entrees].map((f) => f.id));
    const tirables = vivier.filter((f) => !dejaLa.has(f.id));
    // Tirage sans remise dans la fenêtre demandée. `rang` sépare les étapes entre elles ;
    // `k` sépare les tirages successifs de la même étape.
    const rang = ATELIER_STAGES.indexOf(stage);
    const restants = [...tirables];
    const tires = [];
    // ⟦A-78⟧ La profondeur est un SURENSEMBLE : k = 0..1 rendent les mêmes tirages qu'avant,
    // un nœud ajoute k = 2 (voire 3 au Montage) sans déplacer personne.
    for (let k = 0; k < profondeurOffre(stage, mods) && restants.length > 0; k++) {
        const u = draw(seed, periode, "pnj_pick", INDEX_OFFRE + rang * 10 + k);
        const i = Math.min(restants.length - 1, Math.floor(u * restants.length));
        tires.push(restants[i]);
        restants.splice(i, 1);
    }
    return [
        ...fidelises.map((f) => lire(f, true)),
        ...entrees.map((f) => lire(f, false)),
        ...tires.map((f) => lire(f, false)),
    ].sort((a, b) => a.prix - b.prix || a.id.localeCompare(b.id));
}
/**
 * ⟦A-86⟧ **Le tarif d'entrée d'une étape**, lu sur le contenu — `null` si l'étape n'en a pas
 * (contenu en cours d'écriture : le marché tourne alors comme avant, sans garantie). Un seul
 * par étape, `load.ts` y veille.
 */
export function tarifEntree(content, stage) {
    return freelancesDuContenu(content).find((f) => f.etape === stage && f.tarif_entree === true) ?? null;
}
/**
 * Tout le marché visible, étape par étape — **la structure que le client affiche**. Les
 * étapes fermées n'y figurent pas du tout : on ne montre pas un rayon qu'on ne peut pas
 * ouvrir (même principe que la carte « Voir quelqu'un » absente avant le Grand Saut ⟦A-29⟧).
 */
export function marche(state, content, seed, mods) {
    const vue = {};
    for (const stage of etapesOuvertes(state, mods)) {
        const offres = offresPourEtape(state, content, seed, stage, undefined, mods);
        if (offres.length > 0)
            vue[stage] = offres;
    }
    return vue;
}
/**
 * Le freelance retenu pour une étape déléguée.
 *
 * `AtelierScores.freelances?.[stage]` porte le choix explicite du joueur. **Absent, le moins
 * cher du marché est retenu** : c'est le défaut le plus sûr — il ne peut jamais coûter plus
 * que ce que le joueur a vu, et il garde les appelants historiques (`{ delegated: ["montage"] }`,
 * toutes les fixtures de test, l'API telle qu'elle existe) parfaitement valides.
 */
export function offreRetenue(state, content, seed, stage, scores, periode, mods) {
    const offres = offresPourEtape(state, content, seed, stage, periode, mods);
    if (offres.length === 0)
        return null;
    const demande = scores.freelances?.[stage];
    if (demande === undefined)
        return offres[0] ?? null; // trié par prix croissant
    return offres.find((o) => o.id === demande) ?? null;
}
/**
 * ⟦SPEC-PRODUCTEUR §4.5⟧ **Le choix du PRODUCTEUR — qui n'est pas celui du joueur.**
 *
 * `offreRetenue` (au-dessus) sert le **joueur** : demande absente ⇒ le moins cher, parce que
 * le défaut ne doit jamais coûter plus que ce qu'il a vu à l'écran. Le producteur, lui, achète
 * de la **régularité**, et la règle est un tri — aucun tirage :
 *
 *  1. **écarter les pépites** — 🔑 3e source d'infériorité (§4.5 / §7.2 n° 3) : *« il ne
 *     repère JAMAIS une pépite »*. Le tri par cote ne suffit pas à le garantir — une pépite
 *     fidélisée peut monter à 78 — donc l'exclusion est **explicite**, pas une conséquence.
 *     Dénicher les talents reste au joueur, y compris quand le talent est déjà déniché ;
 *  2. écarter ceux dont le **prix dépasse le budget restant** de la vidéo ;
 *  3. la **cote la plus haute** ;
 *  4. à cote égale, la **variance la plus basse** — il achète de la régularité, pas du
 *     potentiel ;
 *  5. à égalité, le **prix le plus bas**, puis l'`id` en ordre lexicographique.
 *
 * ⟦§5.4bis⟧ `repliMoinsCher` est la faiblesse du **manager**, jamais celle du producteur :
 * sous tension de trésorerie, l'étage 3 replie le choix sur le moins cher qui rentre — un
 * choix d'intendance qui coûte de la qualité. Le paramètre existe ici pour que le repli soit
 * LE MÊME tri, portes inversées, et pas une deuxième règle qui dériverait de la première.
 *
 * ⚠️ Avant le recompte du 20/08, le producteur n'exprimait aucun choix (`scores.freelances`
 * absent) et tombait donc sur le défaut joueur — **l'exact opposé de la règle** : cote 45-48
 * partout au lieu de 80-82, et la pépite prise dès qu'elle était au marché.
 */
export function offreProducteur(state, content, seed, stage, budgetRestant, repliMoinsCher = false, mods) {
    const payables = offresPourEtape(state, content, seed, stage, undefined, mods).filter((o) => !o.pepite && o.prix <= budgetRestant);
    const tri = (a, b) => repliMoinsCher
        ? a.prix - b.prix || b.cote - a.cote || a.variance - b.variance || a.id.localeCompare(b.id)
        : b.cote - a.cote || a.variance - b.variance || a.prix - b.prix || a.id.localeCompare(b.id);
    return [...payables].sort(tri)[0] ?? null;
}
// ─────────────────────────────────────────────────────────────────────────────
// Ce que le freelance rend, et ce qu'il coûte
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Le score d'étape livré : **cote ± variance**, uniforme, borné à `[0;100]`.
 *
 * ⚠️ Ce score repasse ensuite par le **plafond de compétence** de l'étape
 * (`scoreEffectifAtelier`, ⟦A-9/A-33⟧) comme n'importe quel score joué : payer quelqu'un
 * n'achète pas le droit de dépasser ce que la chaîne sait exploiter. Une chaîne qui tourne
 * dans une chambre d'enfant ⟦A-11 §2⟧ rend un montage de chambre d'enfant, même signé.
 */
export function livraison(offre, seed, turn) {
    const rang = ATELIER_STAGES.indexOf(offre.etape);
    const u = draw(seed, turn, "pnj_pick", INDEX_LIVRAISON + rang);
    const ecart = uniformFloat(u, -offre.variance, offre.variance);
    return Math.max(0, Math.min(100, Math.round(offre.cote + ecart)));
}
/**
 * ⟦A-61 · A-50 §4⟧ **La direction** — la livraison, relue par quelqu'un qui sait ce qu'il
 * demande.
 *
 * Le défaut que ça répare est nommé par A-50 §4 et il est réel : *« déléguer rend le build
 * obsolète »*. La cote du freelance **remplaçait** le score du joueur, donc tout ce qui avait
 * été monté sur cette étape — et la branche Écriture, par exemple, ne vend presque que ça —
 * cessait de payer à la première embauche. Le jeu punissait d'avoir progressé au moment
 * précis où il proposait de souffler.
 *
 * La forme retenue, et pourquoi celle-là plutôt qu'un bonus de cote plat : **le freelance
 * parcourt une part du chemin qui le sépare de ce que la chaîne saurait exploiter**
 * (`plafondCompetence`, ⟦A-9/A-33⟧). Un bonus plat aurait donné la même chose à un joueur qui
 * n'a rien monté et à un joueur qui a fini sa branche — donc n'aurait rien réparé du tout.
 * Ici la longueur du chemin **est** l'investissement : le plafond monte de 15 points avec la
 * branche possédée, et la direction en prend une part.
 *
 *     dirigee = livraison + part × (cible − livraison)      si livraison < cible
 *     dirigee = livraison                                    sinon
 *
 * Deux bornes, et elles ne sont pas décoratives :
 *
 *  - **`cible` est écrêtée à `COTE_FREELANCE_PLAFOND` (82)** ⟦A-45 règle 1⟧. Sans cet
 *    écrêtage, un joueur à plafond 100 pousserait son monteur au-dessus d'une exécution
 *    forte, et *« un joueur qui joue bien reste toujours meilleur que le meilleur
 *    freelance »* cesserait d'être vrai. La direction rend un débutant pas cher aussi bon
 *    qu'un pro — jamais meilleur qu'un pro.
 *  - **`part` ≤ 1** par construction du schéma, et la fonction est **monotone** en
 *    `livraison` : mieux payé rend toujours mieux, l'ordre du marché n'est pas retourné.
 *
 * **Neutre à `part = 0`** : rend `livraison` au bit près — c'est le seul détecteur de dérive
 * du projet, et ici il tient par l'arithmétique, pas par l'intention.
 */
export function livraisonDirigee(brut, part, plafondEtape) {
    if (!(part > 0))
        return brut;
    const cible = Math.min(COTE_MAX, plafondEtape);
    if (brut >= cible)
        return brut;
    return Math.round(brut + Math.min(1, part) * (cible - brut));
}
/**
 * **Le devis, lisible avant l'embauche.** C'est la fonction que le client doit appeler pour
 * remplir l'écran de production : sans elle, on reproduit le défaut du transitoire (un
 * prélèvement muet à la publication).
 *
 * Les étapes qu'un short n'a pas (`miniature`, `promotion`) sont ignorées, comme dans
 * `scoresEffectifs` — on ne facture pas une miniature qui n'existe pas.
 */
export function devisEmbauches(state, content, seed, scores, format, periode, mods) {
    const lignes = [];
    let total = 0;
    for (const stage of scores.delegated) {
        if (format === "short" && (stage === "miniature" || stage === "promotion"))
            continue;
        const offre = offreRetenue(state, content, seed, stage, scores, periode, mods);
        if (offre === null)
            continue;
        lignes.push({ stage, offre });
        total += offre.prix;
    }
    return { lignes, total };
}
/**
 * L'embauche demandée est-elle légitime ? Exportée pour que `validateAction` l'appelle — le
 * pipeline ne la fait pas respecter lui-même, parce qu'un refus après incrément du `turn`
 * violerait la règle « une action invalide ne consomme rien » (§2.3). C'était déjà le contrat
 * de `delegationValide` ; il ne change pas, seuls les motifs changent.
 */
export function embaucheValide(scores, mods, state, content, seed, format) {
    const ouvertes = new Set(etapesOuvertes(state, mods));
    let total = 0;
    for (const stage of scores.delegated) {
        if (format === "short" && (stage === "miniature" || stage === "promotion"))
            continue;
        if (!ouvertes.has(stage))
            return { ok: false, raison: "capacite", stage };
        const offre = offreRetenue(state, content, seed, stage, scores, undefined, mods);
        if (offre === null) {
            return {
                ok: false,
                raison: "indisponible",
                stage,
                details: { freelance_id: scores.freelances?.[stage] ?? null },
            };
        }
        total += offre.prix;
    }
    if (total > 0 && state.money < total) {
        return {
            ok: false,
            raison: "argent",
            stage: scores.delegated[0],
            details: { total, solde: Math.round(state.money) },
        };
    }
    return { ok: true };
}
// ─────────────────────────────────────────────────────────────────────────────
// Écriture d'état — le SEUL endroit qui touche `state.freelances`
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Enregistre une embauche : c'est ce compteur, et lui seul, qui fait monter la cote d'une
 * pépite et la garde au marché. Appelé par le pipeline de publication, **après** que le
 * devis a été honoré.
 */
export function enregistrerEmbauche(state, id) {
    const courant = state.freelances ?? { embauches: {} };
    courant.embauches[id] = (courant.embauches[id] ?? 0) + 1;
    state.freelances = courant;
}
//# sourceMappingURL=freelances.js.map